import React, { useEffect, useRef } from 'react';
import { useAuth } from '@/core/auth/AuthContext';
import { useSocket } from '@/contexts/SocketContext';
import { useAppStore } from '@/app/store';
import { 
  notificationConfig, 
  TASK_EVENTS, 
  CHAT_EVENTS,
  type UserRole 
} from '@/core/notifications/config';

interface NotificationProviderProps {
  children: React.ReactNode;
}

/**
 * Global notification provider that handles all WebSocket notifications
 * based on user role. This provider should wrap the entire app.
 */
export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { socket, isConnected } = useSocket();
  const addNotification = useAppStore(state => state.addNotification);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const notifiedEvents = useRef<Set<string>>(new Set());
  const MAX_NOTIFIED_EVENTS = 1000;

  // Initialize audio for notifications
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        audioRef.current = new Audio('/notification-sound.mp3');
        audioRef.current.volume = 0.5;
      } catch (error) {
        console.log('Notification sound not available');
        audioRef.current = null;
      }
    }
  }, []);

  // Request browser notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Main notification handler
  useEffect(() => {
    if (!socket || !isConnected || !user) return;

    const userRole = user.role as UserRole;
    const config = notificationConfig[userRole];
    
    if (!config || config.events.length === 0) {
      return; // No notifications configured for this role
    }

    // Helper function to show notification
    const showNotification = (
      title: string, 
      message: string, 
      tag?: string,
      onClick?: () => void
    ) => {
      // Show in-app toast
      addNotification({
        type: 'info',
        title,
        message
      });

      // Sound disabled — visual notifications only

      // Show browser notification if configured and permitted
      if (
        config.showBrowserNotifications && 
        'Notification' in window && 
        Notification.permission === 'granted'
      ) {
        try {
          const notification = new Notification(title, {
            body: message,
            icon: '/favicon.ico',
            tag: tag || `notification-${Date.now()}`,
            requireInteraction: false,
            silent: false
          });

          // Auto-close after 5 seconds
          setTimeout(() => notification.close(), 5000);

          // Handle click
          if (onClick) {
            notification.onclick = () => {
              window.focus();
              onClick();
              notification.close();
            };
          }
        } catch (error) {
          console.error('Error showing browser notification:', error);
        }
      }
    };

    // Task created handler
    const handleTaskCreated = (data: {
      task: any;
      createdBy: { id: string; name: string };
      timestamp: string;
    }) => {
      // Don't notify if user created the task themselves
      if (data.createdBy.id === user.id) return;

      const eventKey = `task-created-${data.task.id}-${data.timestamp}`;
      if (notifiedEvents.current.has(eventKey)) return;
      
      notifiedEvents.current.add(eventKey);
      
      showNotification(
        'Nouvelle tâche assignée',
        `${data.createdBy.name}: ${data.task.message?.substring(0, 100) || 'Nouvelle tâche'}`,
        `task-${data.task.id}`,
        () => {
          window.location.href = `/admin/mes-taches?taskId=${data.task.id}`;
        }
      );

      // Cleanup old events
      if (notifiedEvents.current.size > MAX_NOTIFIED_EVENTS) {
        const firstKey = notifiedEvents.current.values().next().value;
        if (firstKey) {
          notifiedEvents.current.delete(firstKey);
        }
      }
    };

    // Task comment notification handler
    const handleTaskCommentNotification = (data: {
      taskId: string;
      comment: any;
      taskTitle: string;
    }) => {
      const eventKey = `comment-${data.taskId}-${data.comment.id}-${Date.now()}`;
      if (notifiedEvents.current.has(eventKey)) return;
      
      notifiedEvents.current.add(eventKey);
      
      showNotification(
        'Nouveau commentaire',
        `${data.comment.user_name}: ${data.comment.message?.substring(0, 100) || ''}`,
        `comment-${data.taskId}-${data.comment.id}`,
        () => {
          window.location.href = `/admin/mes-taches?taskId=${data.taskId}`;
        }
      );

      // Cleanup old events
      if (notifiedEvents.current.size > MAX_NOTIFIED_EVENTS) {
        const firstKey = notifiedEvents.current.values().next().value;
        if (firstKey) {
          notifiedEvents.current.delete(firstKey);
        }
      }
    };

    // Task updated handler
    const handleTaskUpdated = (data: {
      task: any;
      updatedBy?: { id: string; name: string };
      timestamp: string;
    }) => {
      // Simple refresh notification without toast (just update UI)
      // This prevents notification spam for every small update
      console.log('Task updated:', data.task.id);
    };

    // Task deleted handler
    const handleTaskDeleted = (data: {
      taskId: string;
      deletedBy?: { id: string; name: string };
      timestamp: string;
    }) => {
      // Simple refresh notification without toast
      console.log('Task deleted:', data.taskId);
    };

    // Chat message handler
    const handleChatMessage = (data: {
      threadId: string;
      message: any;
      affaireId: string;
      timestamp: string;
    }) => {
      // Don't notify for own messages
      if (data.message.user_id === user.id) return;

      const eventKey = `chat-${data.message.id}-${data.timestamp}`;
      if (notifiedEvents.current.has(eventKey)) return;
      
      notifiedEvents.current.add(eventKey);
      
      showNotification(
        'Nouveau message chat',
        `${data.message.first_name} ${data.message.last_name}: ${data.message.message?.substring(0, 100) || ''}`,
        `chat-${data.threadId}`,
        () => {
          // Navigate to chat or relevant page
          window.location.href = `/admin/mes-taches`;
        }
      );

      // Cleanup old events
      if (notifiedEvents.current.size > MAX_NOTIFIED_EVENTS) {
        const firstKey = notifiedEvents.current.values().next().value;
        if (firstKey) {
          notifiedEvents.current.delete(firstKey);
        }
      }
    };

    // Map events to handlers
    const eventHandlers: Record<string, (data: any) => void> = {
      [TASK_EVENTS.CREATED]: handleTaskCreated,
      [TASK_EVENTS.COMMENT_NOTIFICATION]: handleTaskCommentNotification,
      [TASK_EVENTS.UPDATED]: handleTaskUpdated,
      [TASK_EVENTS.DELETED]: handleTaskDeleted,
      [CHAT_EVENTS.MESSAGE]: handleChatMessage,
    };

    // Attach only the configured event listeners for this role
    config.events.forEach(event => {
      const handler = eventHandlers[event];
      if (handler) {
        socket.on(event, handler);
      }
    });

    // Cleanup
    return () => {
      config.events.forEach(event => {
        const handler = eventHandlers[event];
        if (handler) {
          socket.off(event, handler);
        }
      });
    };
  }, [socket, isConnected, user, addNotification]);

  return <>{children}</>;
};